import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PassportSectionFallback } from './PassportSectionFallback';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Wrench, TrendingUp, TrendingDown, Minus, Timer } from 'lucide-react';
import { useMinuteProductivityMetrics } from '@/hooks/useMinuteProductivityMetrics';
import { useAuth } from '@/contexts/AuthContext';
import { format, subDays, parseISO } from 'date-fns';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { isSectionReadNotPermitted } from '@/lib/passport/sectionReadAccess';

interface PassportTimeSignatureProps {
  technicianProfileId: string;
}

// FIX-R37-DOC-1: PASSPORT IDENTITY CONTRACT
// This component is a SHOP-OVERLAY widget on top of the Passport view —
// it only has meaning when the viewer has an `activeShop` (productivity
// metrics are computed per-shop). It returns null cleanly when the user
// has no shop, so independent passport users see no widget rather than
// an error. Do NOT use this pattern as license to add more shop-coupled
// state into Passport pages — Passport itself remains shop-agnostic.
export function PassportTimeSignature({ technicianProfileId }: PassportTimeSignatureProps) {
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  
  // R12-M-1: Use shop TZ for date range, not browser TZ
  const endDate = getTodayInShopTz(shopTimezone);
  const startDateObj = parseISO(endDate + 'T00:00:00');
  startDateObj.setDate(startDateObj.getDate() - 90);
  const startDate = format(startDateObj, 'yyyy-MM-dd');

  const { data: metrics, isLoading, isError, error } = useMinuteProductivityMetrics({
    shopId: activeShop?.id || '',
    technicianProfileId,
    startDate,
    endDate,
    shopTimezone,
  });

  if (!activeShop) return null;

  // CORE-C GAP-003: distinct from "No time data available for the last 90
  // days", which is a claim about the person's work and not about the read.
  // Core-100 R4 wave 3: a read the server does not permit this viewer is neither
  // "no time data" (a claim about the person) nor a load failure.
  if (isError && isSectionReadNotPermitted(error)) {
    return (
      <Card data-testid="passport-time-signature">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Time Signature
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground" data-testid="passport-time-signature-not-permitted">
            Not visible to you. Your access does not include this person&apos;s time records; this says nothing about their work.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (isError) {
    return <PassportSectionFallback sectionName="Time Signature" />;
  }

  if (isLoading) {
    return (
      <Card data-testid="passport-time-signature">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Time Signature
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // CORE-AW §44. This read `availableMinutes === 0` as "no time data", and
  // `availableMinutes` is 0 when every technician's working hours are
  // unrecorded — so the surface reported the shop's records as empty when the
  // records were full and the SCHEDULE was missing. Those are different
  // failures with different fixes, and only one of them is about this person.
  // Raw observed work is still shown below; it was measured.
  if (metrics && metrics.capacityUnknown) {
    return (
      <Card data-testid="passport-time-signature">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Time Signature
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground" data-testid="time-signature-capacity-unknown">
            Working hours are not set, so available capacity is unknown —
            utilisation, book load and idle time cannot be calculated.
          </p>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Wrenching (actual)</span>
              <span className="font-medium text-success">{metrics.wrenchingMinutes.toLocaleString()} min</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Booked</span>
              <span className="font-medium">{metrics.bookedMinutes.toLocaleString()} min</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Paused</span>
              <span className="font-medium text-warning">{metrics.pauseMinutes.toLocaleString()} min</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!metrics || metrics.availableMinutes === 0) {
    return (
      <Card data-testid="passport-time-signature">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Time Signature
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No time data available for the last 90 days
          </p>
        </CardContent>
      </Card>
    );
  }

  const getOverUnderIcon = () => {
    if (metrics.overUnderMinutes > 0) return <TrendingUp className="h-4 w-4 text-success" />;
    if (metrics.overUnderMinutes < 0) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getUtilizationColor = (pct: number) => {
    if (pct >= 80) return 'text-success';
    if (pct >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getUtilizationLabel = (pct: number) => {
    if (pct >= 80) return 'Highly Productive';
    if (pct >= 60) return 'Good Utilization';
    if (pct >= 40) return 'Moderate';
    return 'Low Utilization';
  };

  return (
    <Card data-testid="passport-time-signature">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Time Signature
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            Last 90 Days
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Avg Utilization */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">Utilization</span>
            </div>
            <p className={`text-2xl font-bold ${metrics.utilizationPct === null ? 'text-muted-foreground' : getUtilizationColor(metrics.utilizationPct)}`}>
              {metrics.utilizationPct === null ? '—' : `${metrics.utilizationPct}%`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.utilizationPct === null
                ? 'capacity unknown'
                : getUtilizationLabel(metrics.utilizationPct)}
            </p>
          </div>

          {/* Book Load */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Timer className="h-4 w-4 text-accent" />
              <span className="text-xs text-muted-foreground">Book Load</span>
            </div>
            <p className="text-2xl font-bold">
              {metrics.bookLoadPct === null ? '—' : `${metrics.bookLoadPct}%`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.bookLoadPct === null
                ? 'capacity unknown'
                : metrics.capacityComplete
                  ? 'avg capacity used'
                  : 'of known capacity'}
            </p>
          </div>

          {/* Total Wrenching */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Wrench className="h-4 w-4 text-success" />
              <span className="text-xs text-muted-foreground">Wrenching</span>
            </div>
            <p className="text-2xl font-bold">{metrics.wrenchingMinutes.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">total minutes</p>
          </div>

          {/* Over/Under Trend */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              {getOverUnderIcon()}
              <span className="text-xs text-muted-foreground">vs Book</span>
            </div>
            <p className={`text-2xl font-bold ${
              metrics.overUnderMinutes > 0 ? 'text-success' :
              metrics.overUnderMinutes < 0 ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {metrics.overUnderMinutes > 0 ? '+' : ''}{metrics.overUnderMinutes}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.overUnderMinutes > 0 ? 'faster than book' : 
               metrics.overUnderMinutes < 0 ? 'slower than book' : 'on target'}
            </p>
          </div>
        </div>

        {/* Time Breakdown */}
        <div className="space-y-3">
          <h4 className="font-semibold text-foreground text-sm">Time Breakdown</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Available</span>
              <span
                className="font-medium"
                title={metrics.capacityComplete
                  ? undefined
                  : `Excludes ${metrics.unknownCapacityDays} technician-day(s) with no working hours set`}
              >
                {metrics.availableMinutes.toLocaleString()} min
                {!metrics.capacityComplete && ' *'}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Wrenching (actual)</span>
              <span className="font-medium text-success">{metrics.wrenchingMinutes.toLocaleString()} min</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Paused</span>
              <span className="font-medium text-warning">{metrics.pauseMinutes.toLocaleString()} min</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Idle</span>
              <span className="font-medium text-muted-foreground">
                {metrics.idleMinutes === null ? 'unknown' : `${metrics.idleMinutes.toLocaleString()} min`}
              </span>
            </div>
          </div>

          {/* Visual bar */}
          {/* CORE-AW §44: a proportion of an unknown total is not a proportion. */}
          {metrics.availableMinutes > 0 && (
            <div className="h-3 bg-muted rounded-full overflow-hidden flex">
              <div
                className="bg-success"
                style={{ width: `${(metrics.wrenchingMinutes / metrics.availableMinutes) * 100}%` }}
              />
              <div
                className="bg-warning"
                style={{ width: `${(metrics.pauseMinutes / metrics.availableMinutes) * 100}%` }}
              />
              <div
                className="bg-muted-foreground/30"
                style={{ width: `${((metrics.idleMinutes ?? 0) / metrics.availableMinutes) * 100}%` }}
              />
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-success" />
              <span>Wrenching</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-warning" />
              <span>Paused</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
              <span>Idle</span>
            </div>
          </div>
        </div>

        {/* Data source note */}
        <p className="text-xs text-muted-foreground">
          Data source: {metrics.dataSource === 'time_clock' ? 'Time clock entries' : 'Estimated from scheduled shifts'}
        </p>
      </CardContent>
    </Card>
  );
}
